import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StandardRatesColumns } from "us.collection.admin/components/StandardRates/Constants";
import { isDeleteDisabled } from "us.collection.admin/functions";
import { RateActions } from "us.collection.admin/constants";
import { MoreOutlined } from "us.icons";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { ItemMenu } from "./Components";
import { IRateTable } from "./Interfaces";
import {
  $Button,
  $Popover,
  $TableTree,
  $Skeleton,
  $DateLabel,
  ITableTreeColumns,
  $Tag,
} from "us.common/components";
import { INITIAL_ITEM_MENU } from "./Constants";

/**
 * @description - Rates table component for show all standard rates.
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3479208007/View+Standard+Rate+UI+Implementation
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 03/08/2024
 */
const RatesTable: React.FC<IRateTable & PropsFromRedux> = (props) => {
  const { rates, onCallAction } = props;
  const { t } = useTranslation();

  const [itemMenu, setItemMenu] =
    useState<typeof INITIAL_ITEM_MENU>(INITIAL_ITEM_MENU);

  // auto close item menu after 5 seconds
  const setAutoClose = (rateId: number, isTooltip?: boolean) => {
    setTimeout(
      () => {
        setItemMenu({
          visible: false,
          rateId,
          visibleTooltip: false,
        });
      },
      isTooltip ? 8000 : 5000
    );
  };

  const columns: ITableTreeColumns = [
    {
      title: "",
      key: StandardRatesColumns.MENU,
      dataIndex: StandardRatesColumns.MENU,
      width: "50px",
      customRenderChild: (_text: any, record: any) => (
        <div className="align-items-center" key={0}>
          <$Popover
            placement="bottomLeft"
            trigger="click"
            open={itemMenu.visible && itemMenu?.rateId === record.rateId}
            onOpenChange={(visible: boolean) => {
              setItemMenu({
                ...itemMenu,
                visible,
                rateId: record.rateId,
              });
              setAutoClose(0);
            }}
            content={
              <ItemMenu
                actions={
                  isDeleteDisabled(record?.effectiveDate)
                    ? [RateActions.EDIT]
                    : undefined
                }
                onCallAction={(actionType: RateActions) => {
                  setItemMenu({
                    ...itemMenu,
                    visible: false,
                    rateId: record.rateId,
                  });
                  onCallAction(actionType, record);
                }}
              />
            }
            destroyTooltipOnHide
          >
            <$Button
              data-testid="popover-btn"
              icon={<MoreOutlined />}
              size="small"
            />
          </$Popover>
        </div>
      ),
    },
    {
      title: t("US.COLLECTION.ADMIN:STANDARD_RATE.RATE_ID"),
      key: StandardRatesColumns.RATE_ID,
      dataIndex: StandardRatesColumns.RATE_ID,
      width: "180px",
      className: "rate-id",
      customFilter: true,
      customSorter: (a: any, b: any) => a - b,
      customRenderChild: (text: any) => {
        return (
          <span className="d-flex align-items-center">
            <span>{text}</span>
            <$Tag className="tag-status-active ml-2">
              {t("US.COLLECTION.ADMIN:STANDARD_RATE.CURRENT_RATE")}
            </$Tag>
          </span>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:STANDARD_RATE.RATE"),
      key: StandardRatesColumns.RATE,
      dataIndex: StandardRatesColumns.RATE,
      width: "180px",
      customFilter: true,
      customSorter: (a: any, b: any) => a - b,
    },
    {
      title: t("US.COLLECTION.ADMIN:STANDARD_RATE.EFFECTIVE_DATE"),
      key: StandardRatesColumns.EFFECTIVE_DATE,
      dataIndex: StandardRatesColumns.EFFECTIVE_DATE,
      width: "200px",
      customFilter: "date",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customRenderChild: (text: any) => {
        return <$DateLabel value={text} />;
      },
    },
    {
      title: "",
      key: "",
      dataIndex: "",
      width: "100%",
    },
  ];

  return (
    <$Skeleton
      loading={rates.isLoading}
      active
      paragraph={{
        rows: 2,
      }}
    >
      <$TableTree
        rowKey={(record: any) => record.rateId}
        data={rates.data}
        size="small"
        filterOnType
        bordered
        className="mt-3"
        onSort={(sortData, dataSource) => {
          return sortData(dataSource);
        }}
        onFilter={(searchData, dataSource) => {
          return searchData(dataSource);
        }}
        pagination={false}
        rowClassName={({ isCurrentRate }) =>
          isCurrentRate ? "current-rate" : ""
        }
        onRow={(record, _rowIndex) => {
          return {
            onDoubleClick: () => onCallAction(RateActions.EDIT, record),
          };
        }}
        scroll={{ x: "100%", y: "calc((100vh - 180px) / 2)" }}
        columns={columns}
        data-testid="rate-table"
        locale={{
          filterConfirm: t("COMMON.OK"),
          filterReset: t("COMMON.RESET"),
        }}
        resetOnSourceChange={true}
        virtual={true}
      />
    </$Skeleton>
  );
};

const mapStateToProps = (state: RootState) => {
  const { standardRates } = state;
  const { rates } = standardRates;

  return {
    rates,
  };
};

const connector = connect(mapStateToProps, {});

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(RatesTable);
